import React, { useEffect, useState } from 'react';
import ChatThread from './ChatThread';

/**
 * The Aerveil calls arriving on a grower's phone.
 *
 * The timeline is a trimmed version of HeroSequence.tsx from the aerveil-website
 * repo. That one drives a whole photographic panel (a live temperature reading, a
 * damage-line rule, first light along the horizon) which only makes sense over a
 * night vineyard photograph. Here the panel sits on cream, so all that is dropped
 * and only the message phases are kept.
 *
 * The device is drawn in CSS rather than composited from the site's device.webp.
 * That photograph is relit for a backlit blue-hour frame (brightness 0.34, a cool
 * rim down one edge, fog across the lower body) and would read as a mistake on a
 * cream ground.
 *
 * Messages arrive whole, never typed out character by character. Real messages
 * do not do that.
 */

// seconds -> phase. Held long enough to actually read: the welcome runs 35 words
// and the spray call 40, and arriving them a couple of seconds apart made the
// thread feel like a notification storm rather than a day's two decisions.
const SCRIPT = [
  [0, 'welcome'],
  [3.0, 'typingSpray'],
  [4.3, 'spray'],
  [9.0, 'typingFrost'],
  [10.3, 'frost'],
];
const CYCLE = 18.5;

const StatusBar = ({ clock }) => (
  <div
    className="flex h-[10cqw] shrink-0 items-center justify-between px-[6cqw]"
    style={{ background: '#1F2C34' }}
  >
    <span className="font-system text-[3.4cqw] font-semibold leading-none text-[#E9EDEF]">
      {clock}
    </span>
    <span className="flex items-end gap-[0.9cqw]">
      {[1.7, 2.4, 3.1].map((h) => (
        <span
          key={h}
          className="w-[0.85cqw] rounded-[0.3cqw] bg-[#E9EDEF]"
          style={{ height: `${h}cqw` }}
        />
      ))}
      <span className="ml-[0.8cqw] h-[2.3cqw] w-[4.4cqw] rounded-[0.6cqw] border-[0.33cqw] border-[#E9EDEF]/70" />
    </span>
  </div>
);

const AerveilPhone = () => {
  const [phase, setPhase] = useState('welcome');

  useEffect(() => {
    // Reduced motion gets the settled state: both calls delivered, nothing moves.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setPhase('frost');
      return undefined;
    }

    const timers = [];
    const run = () => {
      SCRIPT.forEach(([t, p]) => {
        timers.push(window.setTimeout(() => setPhase(p), t * 1000));
      });
      timers.push(window.setTimeout(run, CYCLE * 1000));
    };
    run();
    return () => timers.forEach(clearTimeout);
  }, []);

  // The clock advances with the day, which shows the thread watched rather than
  // asserting it.
  const clock = phase === 'frost' ? '9:12 PM' : '7:05 AM';

  return (
    <div className="mx-auto w-full max-w-[19.5rem]">
      <div
        className="relative rounded-[2.4rem] bg-night px-[0.5rem] pb-[0.6rem] pt-[1.45rem]"
        style={{ boxShadow: '0 30px 60px -28px rgba(18,20,28,0.55)' }}
      >
        {/* Earpiece slot sits above the display, as it does on the real device,
            so the status bar stays a clean strip and needs no notch cut into it.
            A faked notch that did not line up would be instantly wrong. */}
        <span
          aria-hidden="true"
          className="absolute left-1/2 top-[0.62rem] h-[0.2rem] w-14 -translate-x-1/2 rounded-full bg-cream/20"
        />

        {/* 9:19.5 rather than a shorter screen: all three messages then fit
            without depending on the auto-scroll, and the frost call is the
            payoff of the sequence. The glide still runs, it is just no longer
            the only way to reach the last message. */}
        <div
          className="chat-scope overflow-hidden rounded-[1.95rem]"
          style={{ aspectRatio: '9 / 19.5', background: '#0B141A' }}
        >
          <StatusBar clock={clock} />
          <div style={{ height: 'calc(100% - 10cqw)' }}>
            <ChatThread phase={phase} />
          </div>
        </div>
      </div>

      <p className="mt-4 text-center font-mono text-[0.66rem] uppercase tracking-eyebrow text-ink-soft">
        The call lands on WhatsApp
      </p>
    </div>
  );
};

export default AerveilPhone;
